<template>
  <DataFetcher
    :dataKey="dataKey"
    :refreshOnEvents="refreshOnEvents"
    :refreshDelay="refreshDelay"
    :log="log"
    :debounceDelay="debounceDelay"
    :initDelay="initDelay"
    :useCustomEvent="useCustomEvent"
    :filters="{
      page: internalPage+1,
      limit: internalLimit,
      ...(filters || {}),
    }"
    @fetched="onFetched"
    @beforeGetting="onBeforeGetting"
    @startGetting="onStartGetting"
    @loadingError="onLoadingError"
  >
    <template #any="{ refresh, result }">
      <slot
        name="error"
        :refresh="refresh"
        :result="result"
        v-if="loadingError"
      >
        <NoDataView :refresh="refresh" :result="result" />
      </slot>
      <template v-else>
        <slot
          :refresh="refresh"
          :result="result"
          name="empty"
          v-if="!loading && !items.length"
        >
          <NoDataView :refresh="refresh" :result="result" />
        </slot>
        <slot
          v-else
          name="loadingAndItems"
          :items="items"
          :refresh="refresh"
          :limit="currentLimit"
          :total="total"
          :loading="loading"
        >
          <slot v-if="loading" name="loadingContainer">
            <div class="shimmerContainer w-full space-y-3">
              <div v-for="i in 5" :key="i" class="shimmerElement rounded-lg" :style="{ height: `${80 + (i % 3) * 20}px` }"></div>
            </div>
          </slot>
          <div v-else class="w-full overflow-auto">
            <slot
              name="items"
              :items="items"
              :refresh="refresh"
              :limit="currentLimit"
              :total="total"
            >
              <slot
                v-for="(item, i) in items"
                :name="'item'"
                :item="item"
                :refresh="refresh"
                :index="i"
                :key="i"
                :isLast="i == items.length - 1"
                :isFirst="i == 0"
              >
                <!-- Debug view with jsonToHtml -->
                <div class="border m-2 p-2" v-html="jsonToHtml(item)"></div>
              </slot>
            </slot>
          </div>
        </slot>

        <div class="w-full" :class="{ 'pointer-events-none': loading }">
          <slot
            name="pagination"
            :page="currentPage"
            :totalPages="totalPages"
            :limit="currentLimit"
            :total="total"
            :changePage="changePage"
            :changeLimit="changeLimit"
            :nextPage="
              () => {
                changePage(currentPage + 1)
              }
            "
            :prevPage="
              () => {
                changePage(currentPage - 1)
              }
            "
          >
            <div class="px-6 py-3 bg-gray-50 flex justify-between items-center">
              <span class="text-sm text-gray-700">Page {{ currentPage + 1 }} of {{ totalPages }}</span>
              <div class="space-x-2">
                <button
                  @click="changePage(currentPage - 1)"
                  class="px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  &lt;
                </button>
                <button
                  @click="changePage(currentPage + 1)"
                  class="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  &gt;
                </button>
              </div>
            </div>
          </slot>
        </div>
      </template>
    </template>
  </DataFetcher>
</template>

<script setup>
import { ref, computed } from 'vue'
import NoDataView from './NoDataView.vue'

const props = defineProps({
  dataKey: { type: String, default: '' },
  page: { type: [Number, String], default: 0 },
  limit: { type: [Number, String], default: 10 },
  filters: { type: Object, default: () => ({}) },
  refreshDelay: { type: Number, default: null },
  refreshOnEvents: { type: [String, Array], default: null },
  debounceDelay: { type: Number, default: null },
  log: { type: Boolean, default: false },
  initDelay: { type: Number, default: null },
  // New prop: use custom event system
  useCustomEvent: { type: Boolean, default: false }
})

const emit = defineEmits(['update:page', 'update:limit', 'fetched'])

const items = ref([])
const loadingError = ref(false)
const loading = ref(true)
const internalPage = ref(Number.isInteger(Number(props.page)) ? Number(props.page) : 0)
const internalLimit = ref(Number.isInteger(Number(props.limit)) ? Number(props.limit) : 10)
const total = ref(0)

const totalPages = computed(() => {
  if (!total.value || internalLimit.value <= 0) return 1
  return Math.max(1, Math.ceil(total.value / internalLimit.value))
})

const currentPage = computed(() => {
  return internalPage.value
})

const currentLimit = computed(() => {
  return internalLimit.value
})

const onFetched = ({ data }) => {
  if (data) {
    loading.value = false
    loadingError.value = false
    items.value = Array.isArray(data) ? data : data.result || []
    total.value = Array.isArray(data) ? data.length : data.total || items.value.length
    emit('fetched', {
      items: items.value,
      total: total.value,
      page: internalPage.value,
      limit: internalLimit.value,
    })
  }
}

const onBeforeGetting = () => {
  loading.value = true
  loadingError.value = false
}

const onStartGetting = () => {
  loading.value = true
  loadingError.value = false
}

const onLoadingError = () => {
  loadingError.value = true
  loading.value = false
}

const changePage = (next) => {
  const n = Number(next)
  if (!Number.isInteger(n) || n < 0 || n >= totalPages.value) return
  if (n === internalPage.value) return
  internalPage.value = n
}

const changeLimit = (nextLimit) => {
  const n = Number(nextLimit)
  if (!Number.isInteger(n) || n <= 0) return
  internalLimit.value = n
  internalPage.value = 0
}

// jsonToHtml for debug view (from ui/core version)
const jsonToHtml = (obj) => {
  return JSON.stringify(obj, null, 2)
}

defineExpose({
  changePage,
  changeLimit,
  refresh: () => {
    // DataFetcher will handle refresh through its slot
  }
})
</script>
